import { Banknote, LockKeyhole, LogOut, Monitor, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Cart } from "@/components/Cart.tsx";
import { CloseCashDialog } from "@/components/CloseCashDialog.tsx";
import { PaymentDialog } from "@/components/PaymentDialog.tsx";
import { ProductSearch } from "@/components/ProductSearch.tsx";
import { formatPEN } from "@/lib/utils.ts";
import { useCurrentCashSession } from "@/queries/cashSessions.ts";
import { useAuthStore } from "@/stores/auth.ts";
import { useCartStore } from "@/stores/cart.ts";
import { useTerminalStore } from "@/stores/terminal.ts";

export function POSPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clearSession = useAuthStore((s) => s.clearSession);
  const terminal = useTerminalStore((s) => s.terminal);
  const unpairTerminal = useTerminalStore((s) => s.unpair);
  const addProduct = useCartStore((s) => s.addProduct);

  const currentSession = useCurrentCashSession();
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [closeCashOpen, setCloseCashOpen] = useState(false);

  // Si no hay sesión de caja abierta, ir a apertura.
  useEffect(() => {
    if (currentSession.isSuccess && currentSession.data === null) {
      navigate("/open-caja", { replace: true });
    }
  }, [currentSession.isSuccess, currentSession.data, navigate]);

  if (currentSession.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-slate-500">
        Cargando sesión…
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-3">
        <div className="flex items-center gap-4">
          <h1 className="text-lg font-semibold text-slate-800">Sistema Sale POS</h1>
          {terminal && (
            <button
              type="button"
              onClick={() => {
                // Solo permitir cambiar de caja con la sesión cerrada — si hay
                // sesión abierta, cambiar el terminal_id desde acá generaría
                // inconsistencia con la sesión.
                if (currentSession.data) {
                  alert(
                    "Cerrá la caja antes de cambiar de terminal. La sesión abierta " +
                      "está atada a esta caja.",
                  );
                  return;
                }
                unpairTerminal();
                navigate("/select-terminal", { replace: true });
              }}
              title={`Cambiar caja (actual: ${terminal.code})`}
              className="flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-800 hover:border-indigo-300 hover:bg-indigo-100"
            >
              <Monitor className="h-3.5 w-3.5" />
              <span className="font-mono">{terminal.code}</span>
              <RefreshCw className="h-3 w-3 opacity-60" />
            </button>
          )}
          {currentSession.data && (
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-800">
              Caja abierta
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {currentSession.data && (
            <div
              className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-emerald-900"
              title="Efectivo esperado en caja"
            >
              <Banknote className="h-4 w-4" />
              <div className="flex items-baseline gap-1.5">
                <span className="text-[10px] font-medium uppercase tracking-wide text-emerald-700">
                  En caja
                </span>
                <span className="text-sm font-semibold tabular-nums">
                  {formatPEN(currentSession.data.expected_cash)}
                </span>
              </div>
            </div>
          )}
          <span className="text-sm text-slate-600">{user?.full_name}</span>
          {currentSession.data && (
            <button
              type="button"
              onClick={() => setCloseCashOpen(true)}
              className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100"
            >
              <LockKeyhole className="h-4 w-4" /> Cerrar caja
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              clearSession();
              unpairTerminal();
              navigate("/login", { replace: true });
            }}
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-100"
          >
            <LogOut className="h-4 w-4" /> Salir
          </button>
        </div>
      </header>

      <main className="grid flex-1 grid-cols-1 gap-4 p-4 lg:grid-cols-[1fr_24rem]">
        <section className="min-h-[420px] lg:min-h-0">
          <ProductSearch onSelect={(p) => addProduct(p)} />
        </section>
        <aside className="min-h-[420px] lg:min-h-0">
          <Cart onCheckout={() => setPaymentOpen(true)} />
        </aside>
      </main>

      {paymentOpen && currentSession.data && (
        <PaymentDialog
          branchId={currentSession.data.branch_id}
          onClose={() => setPaymentOpen(false)}
        />
      )}

      {closeCashOpen && currentSession.data && (
        <CloseCashDialog
          sessionId={currentSession.data.id}
          onClose={() => setCloseCashOpen(false)}
        />
      )}
    </div>
  );
}
